import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Heart, LogIn, LogOut, MoreVertical, Plus, Tags, Utensils } from 'lucide-react';
import { useIsAuthenticated } from '@/lib/auth';

// A warm greeting, picked once per visit, instead of a static title.
const GREETINGS = ['Naše recepty', 'Co dneska uvaříš?', 'Z naší kuchyně', 'Dobrou chuť', 'Kuchařka'];

// Home top bar. Logged-out shows a single login icon (→ Cloudflare Access).
// Logged-in shows favourites + an owner menu (new recipe / categories / manage
// / logout). Favourites and admin only exist once authenticated.
export default function TopBar({ title }: { title?: string }) {
  const isAuthenticated = useIsAuthenticated();
  const [menuOpen, setMenuOpen] = useState(false);
  const [greeting] = useState(() => GREETINGS[Math.floor(Math.random() * GREETINGS.length)]!);

  return (
    <div className="flex items-center justify-between pt-6">
      <Link to="/" className="font-serif text-2xl font-extrabold text-text">
        {title ?? greeting}
      </Link>
      <div className="flex items-center gap-2">
        {isAuthenticated ? (
          <>
            <Link
              to="/favourites"
              aria-label="Oblíbené"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-soft text-heart transition-colors hover:brightness-95"
            >
              <Heart size={18} />
            </Link>
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="Menu"
                aria-expanded={menuOpen}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-soft text-text transition-colors hover:brightness-95"
              >
                <MoreVertical size={18} />
              </button>
              {menuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10 cursor-pointer"
                    onClick={() => setMenuOpen(false)}
                    aria-hidden
                  />
                  <div className="absolute right-0 z-20 mt-2 w-52 overflow-hidden rounded-2xl border border-border bg-surface py-1 shadow-card-lg">
                    <MenuLink to="/admin/new" icon={<Plus size={16} />}>
                      Nový recept
                    </MenuLink>
                    <MenuLink to="/admin/categories" icon={<Tags size={16} />}>
                      Kategorie
                    </MenuLink>
                    <MenuLink to="/admin" icon={<Utensils size={16} />}>
                      Recepty
                    </MenuLink>
                    <a
                      href="/cdn-cgi/access/logout"
                      className="mt-1 flex items-center gap-2.5 border-t border-border px-4 py-3 text-sm text-text-muted hover:bg-accent-soft"
                    >
                      <LogOut size={16} /> Odhlásit
                    </a>
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <a
            href="/admin"
            aria-label="Přihlásit"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-soft text-text transition-colors hover:brightness-95"
          >
            <LogIn size={18} />
          </a>
        )}
      </div>
    </div>
  );
}

function MenuLink({ to, icon, children }: { to: string; icon: ReactNode; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2.5 px-4 py-3 text-sm text-text hover:bg-accent-soft"
    >
      {icon}
      {children}
    </Link>
  );
}
